import React, { useMemo, useCallback } from 'react';
import { expandFontTheme, theme, useLocalizationContext, useThemeContext, getThemeColor, } from '@contrast-security-inc/design-system-foundations';
import { Checkbox } from '../Checkbox/Checkbox';
import { css, cx } from '@emotion/css';
import { ListboxItemText } from './ListboxItemText';
import { setupSafelyCallItemToID } from './utils/shared';
import * as messages from './translations';
export const listboxItemStyles = (selectedTheme) => css `
  align-items: center;
  background-color: ${theme.color.transparent};
  color: ${getThemeColor(selectedTheme, { light: theme.color.black, dark: theme.color.white })};
  cursor: pointer;
  padding: 4px 16px;
  transition: background-color 0.2s ease 0s;
  display: flex;
  gap: 8px;
`;
const hoverListboxItemStyles = (selectedTheme) => css `
  /* Dark theme hover using a darker teal70, probably what a teal90 would be if it existed */
  background-color: ${getThemeColor(selectedTheme, { light: theme.color.teal10, dark: 'rgb(21, 69, 63)' })};
  color: ${getThemeColor(selectedTheme, { light: theme.color.black, dark: theme.color.white })};
`;
const selectedListboxItemStyles = css `
  cursor: default;
`;
const emptyListboxItemStyles = (selectedTheme) => css `
  ${expandFontTheme(theme.font.bodyItalic)};
  color: ${getThemeColor(selectedTheme, { light: theme.color.neutral70, dark: theme.color.neutral40 })};
  cursor: default;
`;
const disabledColorStyles = (selectedTheme) => css `
  color: ${getThemeColor(selectedTheme, { light: theme.color.neutral50, dark: theme.color.neutral60 })};
  cursor: not-allowed;
`;
export const ListboxItem = ({ AllowCreateProps, allowItemTextWrap, customRender, getItemProps = () => ({}), index, inputValue, isAnchoredItem, item, itemIsDisabled, itemIsHighlighted, itemIsSelected, itemToID, itemToString, selectedItems, testId, useCheckboxSelection, }) => {
    if (!itemToID && typeof item === 'object') {
        console.error('`itemToID` must be defined for use with an `item` object');
    }
    const { selectedTheme } = useThemeContext();
    const { formatMessage } = useLocalizationContext();
    const safelyCallItemToID = useCallback((listboxItem) => {
        return setupSafelyCallItemToID(itemToID, itemToString, listboxItem);
    }, [itemToID, itemToString]);
    const listItemIsDisabled = useMemo(() => (item && itemIsDisabled ? itemIsDisabled(item) : false), [item, itemIsDisabled]);
    const itemIsChecked = useMemo(() => {
        if (!useCheckboxSelection || !selectedItems) {
            return false;
        }
        const isItemSelected = selectedItems?.some((selectedItem) => {
            if (selectedItem === null) {
                return false;
            }
            return safelyCallItemToID(selectedItem) === safelyCallItemToID(item);
        });
        return isItemSelected;
    }, [item, selectedItems, safelyCallItemToID, useCheckboxSelection]);
    return (React.createElement(React.Fragment, null, item ? (React.createElement("li", { ...getItemProps({
            item,
            disabled: listItemIsDisabled,
            index,
            key: `${itemToString(item)}-${index}`.replace(/ /g, '').trim(),
        }), className: cx({
            [listboxItemStyles(selectedTheme)]: !!item,
            [disabledColorStyles(selectedTheme)]: listItemIsDisabled,
            [hoverListboxItemStyles(selectedTheme)]: !listItemIsDisabled && itemIsHighlighted,
            [selectedListboxItemStyles]: !listItemIsDisabled && itemIsSelected,
        }), "data-testid": `${testId}-listboxitem-${isAnchoredItem ? 'anchored-' : ''}${listItemIsDisabled ? 'disabled-' : ''}${index}` },
        useCheckboxSelection && (React.createElement(Checkbox, { "aria-label": `${itemToString(item)}`, isChecked: itemIsChecked, isDisabled: listItemIsDisabled, testId: `${testId}-listbox-item-checkbox-${index}`, 
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onChange: () => { } })),
        customRender ? (customRender(item)) : (React.createElement(ListboxItemText, { AllowCreateProps: AllowCreateProps, allowItemTextWrap: allowItemTextWrap, inputValue: inputValue, isAnchoredItem: isAnchoredItem, isDisabled: listItemIsDisabled, isSelected: itemIsSelected, item: item, itemIndex: index, itemToString: itemToString, testId: testId })))) : (React.createElement("li", { className: cx(listboxItemStyles(selectedTheme), emptyListboxItemStyles(selectedTheme)), "data-testid": `${testId}-listboxitem-empty` }, formatMessage(messages, 'noResults')))));
};
